import { useState, useCallback } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';

const useAdminComments = (projectId, fetchTaskDetail, leadId) => {
    const [addingComment, setAddingComment] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    const [deletingComment, setDeletingComment] = useState(null);

    const handleAddComment = useCallback(async (taskId, commentText, isClosureComment = false) => {
        setAddingComment(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}/comment`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        comment: commentText,
                        projectId: projectId,
                        leadId: leadId,
                        isClosureComment: isClosureComment
                    })
                }
            );
            if (response.ok) {
                await fetchTaskDetail(taskId, false);
            } else {
                throw new Error('Failed to add comment');
            }
        } catch (err) {
            console.error('Comment error:', err);
            throw err;
        } finally {
            setAddingComment(false);
        }
    }, [projectId, fetchTaskDetail, leadId]);

    const handleEditComment = useCallback(async (taskId, commentId, commentText) => {
        if (!commentText.trim()) {
            alert('Comment cannot be empty');
            return;
        }
        setEditingComment(commentId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}/comment/${encodeURIComponent(commentId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        comment: commentText,
                        projectId: projectId,
                        leadId: leadId
                    })
                }
            );
            if (response.ok) {
                await fetchTaskDetail(taskId, false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(errorData.error || 'Failed to edit comment');
            }
        } catch (err) {
            console.error('Edit comment error:', err);
            alert('Failed to edit comment. Please try again.');
        } finally {
            setEditingComment(null);
        }
    }, [projectId, fetchTaskDetail, leadId]);

    const handleDeleteComment = useCallback(async (taskId, commentId) => {
        if (!window.confirm('Are you sure you want to delete this comment?')) {
            return;
        }

        setDeletingComment(commentId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}/comment/${encodeURIComponent(commentId)}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projectId: projectId, leadId: leadId })
                }
            );

            if (response.ok) {
                await fetchTaskDetail(taskId, false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Failed to delete comment';
                alert(errorMessage);
                await fetchTaskDetail(taskId, false);
            }
        } catch (err) {
            console.error('Delete comment error:', err);
            alert('Failed to delete comment. Please try again.');
        } finally {
            setDeletingComment(null);
        }
    }, [projectId, fetchTaskDetail, leadId]);

    return {
        addingComment,
        editingComment,
        deletingComment,
        handleAddComment,
        handleEditComment,
        handleDeleteComment
    };
};

export default useAdminComments;
